import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Checking payment status...');
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setMessage('No payment session found');
      return;
    }

    // Poll payment status
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      
      if (attempts >= maxAttempts) {
        setStatus('error');
        setMessage('Payment verification timed out. Please check your account or contact support.');
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setMessage('Payment successful! Your account has been upgraded to Pro.');
          
          // Reload page after 2 seconds to refresh user profile
          setTimeout(() => {
            window.location.href = '/studio';
          }, 2000);
          return;
        } else if (response.data.status === 'expired') {
          setStatus('error');
          setMessage('Payment session expired. Please try again.');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
        
      } catch (error) {
        console.error('Payment status check error:', error);
        setStatus('error');
        setMessage('Error checking payment status. Please contact support.');
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-6">
      <Card className="bg-white max-w-md w-full p-8 text-center shadow-xl">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h1>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800">
                <strong>Welcome to Pro!</strong> You now have access to all premium features including AI enhancement and transcript editing.
              </p>
            </div>
            <Button
              onClick={() => navigate('/studio')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Go to Studio
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Issue</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Return Home
              </Button>
              <Button
                onClick={() => navigate('/contact')}
                variant="outline"
                className="w-full"
              >
                Contact Support
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
